<template>
    <div class="dt-carousel">
        <div class="swiper" ref="swiperContainer">
            <div class="swiper-wrapper" ref="swiperWrapper">
                <!-- Slot content will be wrapped in swiper-slide divs -->
            </div>
            
            <!-- Navigation arrows -->
            <div v-if="showArrows" class="swiper-button-next dt-carousel-arrow dt-carousel-arrow-next"></div>
            <div v-if="showArrows" class="swiper-button-prev dt-carousel-arrow dt-carousel-arrow-prev"></div>
            
            <!-- Pagination -->
            <div v-if="showPagination" class="swiper-pagination dt-carousel-pagination"></div>
        </div>
        
        <!-- Hidden slot to capture children -->
        <div style="display: none;" ref="slotContent">
            <slot></slot>
        </div>
    </div>
</template>

<script>
import { Swiper } from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default {
    name: "DTCarousel",
    props: {
        slidesPerViewDesktop: {
            type: Number,
            default: 3,
            validator: function (value) {
                return value > 0;
            },
        },
        slidesPerViewTablet: {
            type: Number,
            default: 2,
            validator: function (value) {
                return value > 0;
            },
        },
        slidesPerViewMobile: {
            type: Number,
            default: 1,
            validator: function (value) {
                return value > 0;
            },
        },
        autoplay: {
            type: Boolean,
            default: false,
        },
        autoplayDelay: {
            type: Number,
            default: 3000,
            validator: function (value) {
                return value > 0;
            },
        },
        showArrows: {
            type: Boolean,
            default: true,
        },
        showPagination: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            swiper: null,
        };
    },
    mounted() {
        this.initSwiper();
    },
    beforeUnmount() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }
    },
    watch: {
        slidesPerViewDesktop() {
            this.reinitSwiper();
        },
        slidesPerViewTablet() {
            this.reinitSwiper();
        },
        slidesPerViewMobile() {
            this.reinitSwiper();
        },
        autoplay() {
            this.reinitSwiper();
        },
        autoplayDelay() {
            this.reinitSwiper();
        },
        showArrows() {
            this.reinitSwiper();
        },
        showPagination() {
            this.reinitSwiper();
        },
    },
    methods: {
        initSwiper() {
            this.setupSlides();
            
            const modules = [];
            if (this.showArrows) modules.push(Navigation);
            if (this.showPagination) modules.push(Pagination);
            if (this.autoplay) modules.push(Autoplay);

            const swiperOptions = {
                modules,
                slidesPerView: this.slidesPerViewMobile,
                spaceBetween: 20,
                breakpoints: {
                    641: {
                        slidesPerView: this.slidesPerViewTablet,
                        spaceBetween: 30,
                    },
                    992: {
                        slidesPerView: this.slidesPerViewDesktop,
                        spaceBetween: 30,
                    },
                },
            };

            if (this.showArrows) {
                swiperOptions.navigation = {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                };
            }

            if (this.showPagination) {
                swiperOptions.pagination = {
                    el: '.swiper-pagination',
                    clickable: true,
                };
            }

            if (this.autoplay) {
                swiperOptions.autoplay = {
                    delay: this.autoplayDelay,
                    disableOnInteraction: false,
                };
            }

            this.swiper = new Swiper(this.$refs.swiperContainer, swiperOptions);
        },
        setupSlides() {
            const swiperWrapper = this.$refs.swiperWrapper;
            const slotContent = this.$refs.slotContent;
            
            if (!swiperWrapper || !slotContent) {
                return;
            }
            
            swiperWrapper.innerHTML = '';
            
            const children = Array.from(slotContent.children).filter(child => {
                return child.nodeType === Node.ELEMENT_NODE &&
                       !(child.tagName && child.tagName.toLowerCase() === 'style') &&
                       (child.children.length > 0 || child.textContent.trim() !== '');
            });
            
            children.forEach((child) => {
                const slideDiv = document.createElement('div');
                slideDiv.className = 'swiper-slide';
                slideDiv.appendChild(child.cloneNode(true));
                swiperWrapper.appendChild(slideDiv);
            });
        },
        reinitSwiper() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
            }
            this.$nextTick(() => {
                this.initSwiper();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;

.dt-carousel {
    width: 100%;
    position: relative;

    .swiper {
        width: 100%;
        height: auto;
    }

    .dt-carousel-arrow {
        width: 44px;
        height: 44px;
        background-color: $color-new-black;
        border-radius: 50%;
        color: white;
        font-size: 18px;
        font-weight: bold;
        transition: all 0.3s ease;
        
        &:after {
            font-size: 18px;
            font-weight: bold;
        }

        &:hover {
            background-color: rgba($color-new-black, 0.8);
            transform: scale(1.1);
        }

        &.swiper-button-disabled {
            opacity: 0.3;
            cursor: not-allowed;
            
            &:hover {
                transform: none;
                background-color: $color-new-black;
            }
        }
    }

    .dt-carousel-arrow-next {
        right: 10px;
    }

    .dt-carousel-arrow-prev {
        left: 10px;
    }

    .dt-carousel-pagination {
        bottom: 10px;
        
        :deep(.swiper-pagination-bullet) {
            width: 12px;
            height: 12px;
            background-color: rgba($color-new-black, 0.3);
            opacity: 1;
            transition: all 0.3s ease;
            
            &.swiper-pagination-bullet-active {
                background-color: $color-new-black;
                transform: scale(1.2);
            }
        }
    }

    @media (max-width: $breakpoint-tablet) {
        .dt-carousel-arrow {
            width: 36px;
            height: 36px;
            font-size: 14px;
            
            &:after {
                font-size: 14px;
            }
        }
        
        .dt-carousel-arrow-next {
            right: 5px;
        }

        .dt-carousel-arrow-prev {
            left: 5px;
        }
    }

    @media (max-width: $breakpoint-mobile) {
        .dt-carousel-arrow {
            width: 32px;
            height: 32px;
            font-size: 12px;
            
            &:after {
                font-size: 12px;
            }
        }
    }
}
</style>